import axios from "axios";
import * as Sentry from "@sentry/react";

// Type definitions for the function parameters
interface AttributionData {
  currentUrl: string;
  referrer?: string;
  fbclid?: string | null;
  utm_campaign?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
  username?: string | null;
  phone_number?: string | null;
  eventName: string | null;
  additionalData?: {
    success?: boolean;
    sale?: {
      product_type?: string;
      amount_inr?: number;
    };
  } | null;
}

// Helper function to get a cookie by name
export const getCookie = (name: string): string | null => {
  const cookieArr = document.cookie.split(";");
  for (const cookie of cookieArr) {
    const [key, value] = cookie.trim().split("=");
    if (key === name) {
      return value;
    }
  }
  return null; // Return null if the cookie does not exist
};

// Helper function to set a cookie
export const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date();
  expires.setDate(expires.getDate() + days);
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; domain=nationalfinanceolympiad.com; secure; SameSite=None`;
};

export const sendAttributionData = async ({
  currentUrl = "",
  referrer = "",
  fbclid = null,
  utm_campaign = null,
  utm_source = null,
  utm_medium = null,
  utm_term = null,
  utm_content = null,
  username = null,
  phone_number = null,
  eventName = null,
  additionalData = null,
}: AttributionData): Promise<any> => {
  if (!currentUrl) {
    console.error("currentUrl is required");
    return;
  }

  // Retrieve session_id from cookies
  const session_id = getCookie("session_id") || null;

  try {
    const url = new URL(currentUrl);

    const data = {
      currentUrl: url.hostname,
      currentPath: url.pathname,
      referrer,
      fbclid,
      utm_campaign,
      utm_source,
      utm_medium,
      utm_term,
      utm_content,
      additional_data: {
        event_name: eventName,
        ...additionalData,
      },
    };

    const payload = {
      username,
      session_id,
      phone_number,
      data,
    };

    const response = await axios.post(
      "https://nfo-arbitrium.el.r.appspot.com/channelAttribution/store-attribution",
      payload,
      {
        headers: {
          "x-api-key": "lZxTPXggnP4ygq4PKaVjCQWdRVCYFrz4uxwWf3H3",
          "Content-Type": "application/json",
        },
      }
    );

    console.log("API Response:", response.data);

    if (response.data.session_id && !session_id) {
      setCookie("session_id", response.data.session_id, 30);
      console.log("New session_id saved in cookies:", response.data.session_id);
    }

    return response.data;
  } catch (err: any) {
    console.error("Error sending attribution data:", err);
    throw err;
  }
};

export const trackData = async (
  eventName: string | null,
  userName?: string | null,
  phoneNumber?: string | null,
  additionalData?: {
    success?: boolean;
    payment_type?: string;
    sale?: {
      product_type?: string;
      amount_inr?: number;
      order_id?: string;
    };
  }
) => {
  try {
    const response = await sendAttributionData({
      currentUrl: window.location.href,
      referrer: document.referrer,
      fbclid: new URLSearchParams(window.location.search).get("fbclid"),
      utm_campaign: new URLSearchParams(window.location.search).get(
        "utm_campaign"
      ),
      utm_term: new URLSearchParams(window.location.search).get("utm_term"),
      utm_content: new URLSearchParams(window.location.search).get(
        "utm_content"
      ),
      utm_source: new URLSearchParams(window.location.search).get("utm_source"),
      utm_medium: new URLSearchParams(window.location.search).get("utm_medium"),
      username: userName,
      phone_number: phoneNumber,
      eventName: eventName,
      additionalData: additionalData,
    });
    console.log("Tracking response:", response);
  } catch (err) {
    console.error("Error tracking data:", err);
    Sentry.captureException(err);
  }
};
